import type { AgentConfigOptions } from '@elastic/apm-rum';
import { apmBase } from '@elastic/apm-rum';
import { storeToRefs } from 'pinia';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { capitalize } from '@/core/lib/string';
import { getOddsApiUrl, getPolluxUrl } from '@/core/lib/url';
import { SiteStyle } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';
import { useToggleStore } from '@/store/toggleStore';

function formatEnv(env: string): string {
    if (env === 'uat') return 'UAT';

    return capitalize(env);
}

let networkDown = false;
let networkRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

// APM flushes every 15 s; wait 16 s after coming back online to avoid sending
const NETWORK_RECOVERY_DELAY_MS = 16_000;

function trackNetworkState(): void {
    globalThis.addEventListener('offline', () => {
        if (networkRecoveryTimer) {
            clearTimeout(networkRecoveryTimer);
            networkRecoveryTimer = null;
        }
        networkDown = true;
    });
    globalThis.addEventListener('online', () => {
        networkRecoveryTimer = setTimeout(() => {
            networkDown = false;
            networkRecoveryTimer = null;
        }, NETWORK_RECOVERY_DELAY_MS);
    });
}

function isNetworkDown(): boolean {
    return !navigator.onLine || networkDown;
}

function getIosSystemVersion(): number | null {
    const match = /CPU (?:iPhone |iPad )?OS (\d+)_/.exec(navigator.userAgent);
    return match ? Number(match[1]) : null;
}

function getSafariVersion(): number | null {
    const userAgent = navigator.userAgent;
    if (/Chrome\//.test(userAgent) || /Edg\//.test(userAgent)) return null;
    const match = /Version\/(\d+)/.exec(userAgent);
    return match ? Number(match[1]) : null;
}

function getEdgeVersion(): number | null {
    const match = /Edg\/(\d+)/.exec(navigator.userAgent);
    return match ? Number(match[1]) : null;
}

function getChromeVersion(): number | null {
    const userAgent = navigator.userAgent;
    if (/Edg\//.test(userAgent) || /SamsungBrowser\//.test(userAgent)) return null;
    const match = /Chrome\/(\d+)/.exec(userAgent);
    return match ? Number(match[1]) : null;
}

function getFirefoxVersion(): number | null {
    const match = /Firefox\/(\d+)/.exec(navigator.userAgent);
    return match ? Number(match[1]) : null;
}

function isUnsupportedBrowser(): boolean {
    const iosVersion = getIosSystemVersion();
    if (iosVersion !== null) return iosVersion < 14;

    const safariVersion = getSafariVersion();
    if (safariVersion !== null) return safariVersion < 14;

    const edgeVersion = getEdgeVersion();
    if (edgeVersion !== null) return edgeVersion < 126;

    const chromeVersion = getChromeVersion();
    if (chromeVersion !== null) return chromeVersion < 91;

    const firefoxVersion = getFirefoxVersion();
    if (firefoxVersion !== null) return firefoxVersion < 125;

    return false;
}

const IGNORED_ERROR_PATTERNS = [
    // Network / user-cancelled requests
    /AbortError/,
    /Request aborted/,
    /OperationCanceledException/,
    /ApolloError: Load failed/,
    /ApolloError: A server with the specified hostname could not be found/,
    // Safari th — host not found (same class as the English ApolloError above)
    /ไม่พบเซิร์ฟเวอร์ที่ใช้ชื่อเครื่องโฮสต์ที่ระบุ/,
    /Internet connection appears to be offline/i,
    /Tampaknya koneksi internet sedang offline/i,
    // Safari vi — offline (same class as "Internet connection appears to be offline")
    /Kết nối Internet/,
    /Failed to fetch/,
    /status code 429/,
    /status code 401/,
    /Unable to preload CSS/,
    /WebSocket closed with status code: 100[16]/,
    /New windows are disabled/,
    /timeout of \d+ms exceeded/,
    /Server timeout elapsed without receiving a message from the server/,

    // Browser API noise
    /ResizeObserver loop/,
    /^Script error\.?$/,
    // VueUse 13's useWindowSize calls addEventListener on targets not supported by old Safari (≤13)
    /\.addEventListener is not a function/,

    // window.open(url, 'Statistics', ...) reuses a fixed window name. If that named window is
    // already showing content from another origin (the stats CDN), the browser blocks the
    // property access it needs to refocus/reuse it — a standard cross-origin restriction, not
    // a bug in one user's browser. The popup itself still opens; only the refocus step fails.
    // Only 1 occurrence seen so far, so treat this as provisional until more samples confirm it.
    /Failed to set a named property 'blur' on 'Window'/,

    // isTrusted DOM event rejected as promise (JSON.stringify uses double quotes)
    /"isTrusted"\s*:\s*true/,

    // Known app behaviors, not actionable bugs
    /GetOddsApiToken (?:died|failed)/,
    /Invalid character: '\\0'/,

    // Browsers that cannot parse optional chaining / nullish coalescing — not an app bug
    /SyntaxError.*Unexpected token \?/,

    // Vue 3 internal race conditions; no upstream fix (vuejs/core#5184).
    // Triggered when an external agent (browser auto-translate, extensions) mutates
    // Vue-owned DOM nodes, or when teardown/navigation races read properties off an
    // already-nulled component instance / detached node. emitsOptions and Symbol("_vtc")
    // are Vue-internal names that app code never touches, so message filters are safe.
    /emitsOptions/,
    /Symbol\("_vtc"\)/,
    /can't access property "parentNode".*null/, // Firefox wording
    /Cannot read properties of null \(reading 'parentNode'\)/, // Chrome/V8 wording
    /can't access property "style".*null/, // Firefox wording
    /Cannot read properties of null \(reading 'style'\)/, // Chrome/V8 wording
    /can't access property "nextSibling".*null/, // Firefox wording
    /Cannot read properties of null \(reading 'nextSibling'\)/, // Chrome/V8 wording
    /Cannot create property 'prevDep' on string 'Object'/, // Vue reactivity internal (Chrome/V8 wording)
];

const UNSUPPORTED_REPLACE_ALL_ERROR_PATTERN = /\.replaceAll is not a function/;

const IGNORED_CULPRIT_PATTERNS = [
    // Vite's modulepreload polyfill only calls fetch() to preload chunks — no app logic.
    // Any error originating here is browser/network noise and is never actionable.
    /modulepreload-polyfill/,
];

const ALLOWED_CULPRIT_PATTERNS = [
    /\/src\//, // source-map resolved paths (e.g. ../../src/store/...)
    /node_modules\//, // any node_modules path (Vite deps, pnpm, etc.)
    /^assets\//, // build output served from the project's own origin
];

export function registerApmFilters() {
    trackNetworkState();
    apmBase.addFilter(filterApmPayload);
}

// Takes over the Vue error handler (plugin installed with captureErrors: false) to append component
// name / hook / route to the message. Plugin's own vm.$options.name is empty for <script setup> in prod.
function getVueComponentName(instance: any): string {
    if (!instance) return 'unknown';
    if (instance.$root === instance) return 'Root';
    return instance.$?.type?.__name ?? instance.$options?.name ?? instance.$options?.__name ?? instance.$options?._componentTag ?? 'Anonymous';
}

function describeEventTarget(target: EventTarget | null): string | undefined {
    if (target instanceof HTMLScriptElement || target instanceof HTMLImageElement) return target.src;
    if (target instanceof HTMLElement) return target.tagName.toLowerCase();
    return '';
}

// Never String(value) here: a rejected DOM event (e.g. a blocked <script> load) would collapse to
// "[object Event]", which tells us nothing and cannot be matched by the isTrusted filter above.
// Events are stringified field by field because Firefox exposes no own enumerable properties on
// them, so JSON.stringify alone would flatten them to "{}".
function ensureError(err: unknown): Error {
    if (err instanceof Error) return err;
    if (typeof err === 'string') return new Error(err);
    if (err instanceof Event) {
        return new Error(JSON.stringify({
            isTrusted: err.isTrusted,
            type: err.type,
            target: describeEventTarget(err.target),
        }));
    }

    return new Error(JSON.stringify(err));
}

export function registerVueErrorContext(app: App, router: Router): void {
    app.config.errorHandler = (err, instance, info) => {
        const error = ensureError(err);
        const component = getVueComponentName(instance);
        const routeName = String(router.currentRoute.value.name ?? 'unknown');
        error.message = `${error.message} [component=${component}] [hook=${info || 'unknown'}] [route=${routeName}]`;
        apmBase.captureError(error);
        console.error(error);
    };
}

export function getApmConfig(): AgentConfigOptions {
    const { oddsApiUrl, apmServerUrl, asiaApmSampleRate, euroApmSampleRate } = storeToRefs(useToggleStore());
    const { siteStyle } = storeToRefs(useCustomerStore());
    return {
        serviceName: siteStyle.value === SiteStyle.Euro ? 'Kratos-Euro' : 'Kratos-Asia',
        serviceVersion: `${__ENV_NAME__}-${__COMMIT_SHA__}`,
        serverUrl: apmServerUrl.value,
        disableInstrumentations: ['click', 'xmlhttprequest', 'fetch'],
        distributedTracingOrigins: [
            getOddsApiUrl(oddsApiUrl.value),
            getPolluxUrl(),
        ],
        environment: formatEnv(__ENV_NAME__),
        transactionSampleRate: siteStyle.value === SiteStyle.Euro
            ? euroApmSampleRate.value
            : asiaApmSampleRate.value,
        flushInterval: 15000,
    };
}

export function isProjectCdnCulprit(culprit: string, cdnOrigin = __URL_KRATOS_CDN__): boolean {
    return !!(cdnOrigin && culprit.startsWith(cdnOrigin));
}

function shouldKeepError(err: any) {
    const message = err.exception?.message ?? err.log?.message ?? '';
    const type = err.exception?.type ?? '';
    const fullMessage = type ? `${type}: ${message}` : message;
    const culprit = err.culprit ?? '';

    if (typeof String.prototype.replaceAll !== 'function'
        && (UNSUPPORTED_REPLACE_ALL_ERROR_PATTERN.test(message) || UNSUPPORTED_REPLACE_ALL_ERROR_PATTERN.test(fullMessage))) return false;
    if (IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(message) || pattern.test(fullMessage))) return false;
    if (IGNORED_CULPRIT_PATTERNS.some(pattern => pattern.test(culprit))) return false;
    if (culprit && !ALLOWED_CULPRIT_PATTERNS.some(p => p.test(culprit)) && !isProjectCdnCulprit(culprit)) return false;

    return true;
}

function filterApmPayload(payload: any) {
    if (isNetworkDown()) return null;
    if (!payload.errors) return payload;
    if (isUnsupportedBrowser()) return null;

    payload.errors = payload.errors.filter(shouldKeepError);

    return payload.errors.length > 0 ? payload : null;
}
